import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useMemo } from 'react'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide8bFraudCall.copy'

const ease = [0.16, 1, 0.3, 1] as const

type Phase = 'incoming' | 'active'

export default function Slide8bFraudCall(_props: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['fakeAmount'],
  })

  const transcriptLines = useMemo(
    () => [
      c.transcript1,
      `${c.transcript2Before}${c.fakeAmount}${c.transcript2After}`,
      c.transcript3,
    ],
    [c.transcript1, c.transcript2Before, c.fakeAmount, c.transcript2After],
  )

  const [phase, setPhase] = useState<Phase>('incoming')
  const [visibleLines, setVisibleLines] = useState(0)
  const [showEmail, setShowEmail] = useState(false)
  const [showBreakdown, setShowBreakdown] = useState(false)

  useEffect(() => {
    const answerTimer = setTimeout(() => setPhase('active'), 2500)
    return () => clearTimeout(answerTimer)
  }, [])

  useEffect(() => {
    if (phase !== 'active') return

    const lineCount = transcriptLines.length
    const emailMs = 1200 + lineCount * 1800 + 600
    const breakdownMs = emailMs + 1200

    const timers = [
      ...transcriptLines.map((_, i) =>
        setTimeout(() => setVisibleLines(i + 1), 1200 + i * 1800),
      ),
      setTimeout(() => setShowEmail(true), emailMs),
      setTimeout(() => setShowBreakdown(true), breakdownMs),
    ]
    return () => timers.forEach(clearTimeout)
  }, [phase, transcriptLines])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 mx-auto max-w-5xl">
      <div className="relative grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:items-center lg:gap-9">
        {/* Phone mockup */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.08, ease }}
          className="mx-auto w-full max-w-sm"
        >
          <div className="relative">
            <div className="absolute -start-[3px] top-24 h-8 w-[2px] rounded-full bg-slate-600/45" />

            <div className="relative overflow-hidden rounded-t-[2.5rem] border-[4px] border-b-0 border-slate-900">
              <AnimatePresence mode="wait">
                {phase === 'incoming' ? (
                  <IncomingCallScreen
                    key="incoming"
                    callerName={c.callerName}
                    callerSubtitle={c.callerSubtitle}
                    incomingLabel={c.incomingLabel}
                    declineLabel={c.declineLabel}
                    acceptLabel={c.acceptLabel}
                  />
                ) : (
                  <ActiveCallScreen
                    key="active"
                    callerName={c.callerName}
                    visibleLines={visibleLines}
                    showEmail={showEmail}
                    transcriptLines={transcriptLines}
                    emailFrom={c.emailFrom}
                    emailSubject={c.emailSubject}
                    emailPreview={c.emailPreview}
                    liveLabel={c.liveLabel}
                    nowLabel={c.nowLabel}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Right side breakdown */}
        <div className="mx-auto w-full max-w-xl lg:max-w-none lg:border-s lg:border-slate-200/80 lg:ps-10">
          {showBreakdown && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease }}
                className="pb-6"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.4">
                      <path d="M3 9L9 3M3 3l6 6" />
                    </svg>
                  </span>
                  <p className="text-xl sm:text-[1.4rem] font-semibold text-slate-800">
                    {c.whatTheyWantLabel}
                  </p>
                </div>
                <p className="mt-2 ps-9 text-base sm:text-lg leading-relaxed text-slate-500">
                  {c.whatTheyWant}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.18, ease }}
                className="pt-6"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.4">
                      <path d="M2.5 6.5L5 9l4.5-6" />
                    </svg>
                  </span>
                  <p className="text-xl sm:text-[1.4rem] font-semibold text-slate-800">
                    {c.saferMoveLabel}
                  </p>
                </div>
                <p className="mt-2 ps-9 text-base sm:text-lg leading-relaxed text-slate-500">
                  {c.saferMove}
                </p>
                <div className="mt-4 space-y-3 ps-9">
                  {[c.verifyRoute1, c.verifyRoute2].map((route, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35, delay: 0.3 + i * 0.09, ease }}
                      className="flex items-start gap-2.5"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-emerald-600">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.7">
                          <path d="M2.5 6.5L5 9l4.5-6" />
                        </svg>
                      </span>
                      <p className="text-base sm:text-lg leading-relaxed text-slate-500">{route}</p>
                    </motion.div>
                  ))}
                </div>
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.42, delay: 0.5, ease }}
                  className="mt-5 ps-9 text-base sm:text-lg leading-relaxed text-slate-400"
                >
                  {c.warning}
                </motion.p>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Incoming call screen                                               */
/* ------------------------------------------------------------------ */

function IncomingCallScreen({
  callerName,
  callerSubtitle,
  incomingLabel,
  declineLabel,
  acceptLabel,
}: {
  callerName: string
  callerSubtitle: string
  incomingLabel: string
  declineLabel: string
  acceptLabel: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-gradient-to-b from-slate-800 to-slate-900 px-6 pb-8 pt-4"
    >
      <StatusBar />

      <div className="mt-6 flex flex-col items-center">
        <motion.div
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20"
        >
          <svg className="h-8 w-8 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 20a6 6 0 00-12 0" />
            <circle cx="12" cy="10" r="4" />
          </svg>
        </motion.div>
        <p className="mt-3 text-xl font-semibold text-white">{callerName}</p>
        <p className="mt-0.5 text-[15px] text-white/40">{callerSubtitle}</p>
        <p className="mt-1 text-sm text-white/30">{incomingLabel}</p>
      </div>

      {/* Answer / Decline buttons */}
      <div className="mt-10 flex items-center justify-center gap-16">
        <div className="flex flex-col items-center gap-1.5">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500/20">
            <svg className="h-6 w-6 text-red-400 rotate-[135deg]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" />
            </svg>
          </div>
          <span className="text-xs text-white/40">{declineLabel}</span>
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500"
          >
            <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" />
            </svg>
          </motion.div>
          <span className="text-xs text-white/40">{acceptLabel}</span>
        </div>
      </div>
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/*  Active call screen with transcript + email notification           */
/* ------------------------------------------------------------------ */

function ActiveCallScreen({
  callerName,
  visibleLines,
  showEmail,
  transcriptLines,
  emailFrom,
  emailSubject,
  emailPreview,
  liveLabel,
  nowLabel,
}: {
  callerName: string
  visibleLines: number
  showEmail: boolean
  transcriptLines: string[]
  emailFrom: string
  emailSubject: string
  emailPreview: string
  liveLabel: string
  nowLabel: string
}) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(interval)
  }, [])

  const mins = String(Math.floor(elapsed / 60)).padStart(2, '0')
  const secs = String(elapsed % 60).padStart(2, '0')

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="relative bg-gradient-to-b from-slate-800 to-slate-900 px-6 pb-6 pt-4"
    >
      <StatusBar />

      {/* Caller info */}
      <div className="mt-5 flex flex-col items-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/20">
          <svg className="h-7 w-7 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 20a6 6 0 00-12 0" />
            <circle cx="12" cy="10" r="4" />
          </svg>
        </div>
        <p className="mt-2 text-xl font-semibold text-white">{callerName}</p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.3, ease }}
          className="mt-0.5 text-[15px] tabular-nums text-emerald-400 font-medium"
        >
          {mins}:{secs}
        </motion.p>
      </div>

      {/* Live transcript */}
      <div className="mt-5 space-y-2 min-h-[6.5rem]">
        {transcriptLines.map((line, i) => (
          <AnimatePresence key={i}>
            {i < visibleLines && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease }}
                className="flex items-start gap-2"
              >
                {i === 0 && (
                  <span className="mt-[3px] shrink-0 text-[11px] font-medium uppercase tracking-wider text-emerald-500/60">
                    {liveLabel}
                  </span>
                )}
                <p className={`text-[15px] leading-snug text-white/70 ${i > 0 ? 'ps-[32px]' : ''}`}>
                  &ldquo;{line}&rdquo;
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        ))}
      </div>

      {/* Email notification sliding down */}
      {showEmail && (
      <motion.div
        initial={{ opacity: 0, y: -80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="absolute start-3 end-3 top-[38px] z-30"
      >
        <div className="rounded-2xl bg-white/95 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.25)] px-3.5 py-3">
          <div className="flex items-start gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500">
              <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-[13px] font-semibold text-slate-900">{emailFrom}</p>
                <p className="text-[11px] text-slate-400">{nowLabel}</p>
              </div>
              <p className="text-[13px] font-medium text-slate-700 leading-snug">
                {emailSubject}
              </p>
              <p className="text-[12px] text-slate-500 leading-snug mt-0.5 line-clamp-2">
                {emailPreview}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
      )}
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/*  Shared status bar                                                  */
/* ------------------------------------------------------------------ */

function StatusBar() {
  return (
    <div className="relative flex items-center justify-between">
      <span className="text-[13px] font-semibold text-white/90">9:41</span>
      <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-[22px] w-[90px] rounded-full bg-black" />
      <div className="flex items-center gap-1">
        <svg className="h-3.5 w-3.5 text-white/90" viewBox="0 0 16 16" fill="currentColor">
          <rect x="1" y="10" width="3" height="5" rx="0.5" />
          <rect x="5" y="7" width="3" height="8" rx="0.5" />
          <rect x="9" y="4" width="3" height="11" rx="0.5" />
          <rect x="13" y="1" width="3" height="14" rx="0.5" opacity="0.3" />
        </svg>
        <svg className="h-3.5 w-3.5 text-white/90" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 3C5.5 3 3.3 4 1.8 5.6a.75.75 0 001.1 1C4.2 5.3 6 4.5 8 4.5s3.8.8 5.1 2.1a.75.75 0 001.1-1C12.7 4 10.5 3 8 3z" opacity="0.3" />
          <path d="M8 6.5c-1.7 0-3.2.7-4.3 1.8a.75.75 0 001.1 1C5.7 8.4 6.8 8 8 8s2.3.4 3.2 1.3a.75.75 0 001.1-1C11.2 7.2 9.7 6.5 8 6.5z" opacity="0.6" />
          <path d="M8 10c-.9 0-1.8.4-2.4 1a.75.75 0 001.1 1c.3-.4.8-.5 1.3-.5s1 .2 1.3.5a.75.75 0 001.1-1C9.8 10.4 8.9 10 8 10z" />
        </svg>
        <svg className="h-4.5 w-[22px] text-white/90" viewBox="0 0 25 12" fill="currentColor">
          <rect x="0" y="1" width="21" height="10" rx="2" stroke="currentColor" strokeWidth="1" fill="none" />
          <rect x="22" y="4" width="2" height="4" rx="0.5" opacity="0.4" />
          <rect x="1.5" y="2.5" width="14" height="7" rx="1" />
        </svg>
      </div>
    </div>
  )
}
